/**
 * ErrorBoundary — captura exceptions de render/lifecycle e mostra
 * uma tela de erro em vez de deixar a tela ficar branca.
 *
 * Em prod, o stack trace é só logado no console.
 * Em dev, é exibido pro programador.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";

interface Props {
  children: ReactNode;
  /** Resetar quando essa key mudar (ex: location.pathname). */
  resetKey?: string | number;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log no console pra capturar o stack completo
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    this.setState({ error, info });
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.state.error &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ error: null, info: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    const isDev = import.meta.env.DEV;

    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-50 dark:bg-slate-800/40 p-6">
        <div className="w-full max-w-2xl rounded-xl border border-red-200 dark:border-red-900/40 bg-white dark:bg-slate-900 p-6 shadow-card">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-300">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Algo quebrou nessa página
              </h1>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Foi um erro de runtime no componente. Tenta recarregar — se persistir,
                copia o stack abaixo pro suporte.
              </p>

              <div className="mt-4 rounded-lg border border-surface-200 dark:border-slate-800 bg-surface-50 dark:bg-slate-800/40 p-3 text-xs">
                <div className="font-mono font-semibold text-red-700">
                  {this.state.error.name}: {this.state.error.message}
                </div>
                {isDev && this.state.error.stack && (
                  <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[11px] text-slate-600 dark:text-slate-400">
                    {this.state.error.stack}
                  </pre>
                )}
                {isDev && this.state.info?.componentStack && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-slate-500 dark:text-slate-400">
                      Component stack
                    </summary>
                    <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[11px] text-slate-500 dark:text-slate-400">
                      {this.state.info.componentStack}
                    </pre>
                  </details>
                )}
              </div>

              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand-600 px-3 text-sm font-medium text-white hover:bg-brand-700"
                  onClick={() => window.location.reload()}
                >
                  <RefreshCcw className="h-4 w-4" /> Recarregar
                </button>
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-surface-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-surface-50 dark:hover:bg-slate-800/50"
                  onClick={() => this.setState({ error: null, info: null })}
                >
                  Tentar de novo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
